import { useCallback, useSyncExternalStore } from "react";
import { useAuth } from "../../../contexts/AuthProvider";
import { localStorageService } from "../../../services/LocalStorageService";

type GapsCountsHidden = {
	/** True when this user chose More → Hide gaps: the counts are left out wherever the view prints them. */
	areCountsHidden: boolean;
	setCountsHidden: (hidden: boolean) => void;
};

/** Everyone reading the choice, so the More sheet's toggle reaches every count on screen in the same commit. */
const subscribers = new Set<() => void>();

const subscribe = (onChange: () => void): (() => void) => {
	subscribers.add(onChange);
	return () => {
		subscribers.delete(onChange);
	};
};

/**
 * More → Hide gaps / Show gaps (APP-886): whether the gaps-first view prints its staff counts — every `filled/required`
 * and every `−N` / `✓` shortfall (the week strip, the gap line, the group headers, the unit rows and the sheets that
 * repeat them). Everything else stays: the groups, the people, the missing rows and their colours.
 *
 * Per user, in every department, kept on this device like the view preference, and read through an external store
 * for the same reason that one is: the toggle (the More sheet, portalled from the bar) and the counts (the home and
 * its sheets) are never in one subtree, so per-hook state would leave the screen showing the old choice.
 */
export const useGapsCountsHidden = (): GapsCountsHidden => {
	const { userInfo } = useAuth();
	const userId = userInfo?.id;

	// DEFAULT: before the signed-in user loads there is no choice to read, and counts show — the view as it ships.
	const getSnapshot = useCallback((): boolean => (userId ? localStorageService.getGapsCountsHidden(userId) : false), [userId]);
	const areCountsHidden = useSyncExternalStore(subscribe, getSnapshot);

	const setCountsHidden = useCallback(
		(hidden: boolean): void => {
			if (!userId) return;
			localStorageService.setGapsCountsHidden(hidden, userId);
			for (const onChange of subscribers) onChange();
		},
		[userId],
	);

	return { areCountsHidden, setCountsHidden };
};
